import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProductReport } from "../../redux/apiCalls";
import { publicRequest } from "../../requestMethods";
import moment from "moment";
import excel from "../../components/excel/excel";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";
import { useHistory } from "react-router";
import {
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
    ResponsiveContainer,
    BarChart,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import { FiPlus } from "react-icons/fi";

const getIntroOfPage = (label) => {
    if (label === "Page A") {
        return "Page A is about men's clothing";
    }
    if (label === "Page B") {
        return "Page B is about women's dress";
    }
    if (label === "Page C") {
        return "Page C is about women's bag";
    }
    if (label === "Page D") {
        return "Page D is about household goods";
    }
    if (label === "Page E") {
        return "Page E is about food";
    }
    if (label === "Page F") {
        return "Page F is about baby food";
    }
    return "";
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div
                className="custom-tooltip p-8 font-bold bg-black"
                style={{ background: payload[0].payload.fill }}
            >
                <p className="bold label">{`${payload[0].payload.outlet} : ${payload[0].value} Sales`}</p>
                <p className="intro">{getIntroOfPage(label)}</p>
            </div>
        );
    }

    return null;
};

export default function ProductReport() {
    const dispatch = useDispatch();
    const history = useHistory();
    const currentUser = useSelector((state) => state.user.currentUser);

    const COLORS = [
        "#0088FE",
        "#00C49F",
        "#FFBB28",
        "#FF8042",
        "#8042ff",
        "#ff4263",
        "#56462a",
        "#2a5646",
        "#f8edeb",
        "#df99ad",
        "#ffff00",
    ];

    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const manager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "manager"
            : null
    );

    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin === true : null
    );

    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [inputs, setInputs] = useState({
        createdBy: currentUser._id,
        principle: manager
            ? principle
            : currentUser
            ? currentUser._id
            : undefined,
        dateFrom: moment(Date.now()).startOf("year").format("YYYY-MM-DD"),
        dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
    });

    const getAdmin = async () => {
        const res =
            process.env.REACT_APP_COUNTRY === "SINGAPORE"
                ? await publicRequest.get("/users/admin", {
                      params: { isAdmin: true },
                  })
                : await publicRequest.get("/users/superadmin", {
                      params: { isAdmin: true },
                  });
        setUsersData(res.data);
    };

    const handleChange = (e) => {
        setInputs((prev) => {
            return { ...prev, user: e.value };
        });
    };

    const handleDateFrom = (e) => {
        setInputs((prev) => {
            return { ...prev, dateFrom: e.target.value };
        });
    };
    const handleDateTo = (e) => {
        setInputs((prev) => {
            return { ...prev, dateTo: e.target.value };
        });
    };

    const handleResetFilters = () => {
        setInputs({
            createdBy: currentUser._id,
            principle: manager
                ? principle
                : currentUser
                ? currentUser._id
                : undefined,
            dateFrom: moment(Date.now()).startOf("year").format("YYYY-MM-DD"),
            dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            const res = await getProductReport(dispatch, inputs);

            if (res) {
                setIsLoaded(true);
                setData(res.product);
                setSalesData(res.sales);
            }
        };
        fetchData();
    }, [dispatch, inputs]);

    useEffect(() => {
        if (usersData[0] === undefined) {
            getAdmin();
        }
    }, [usersData]);

    const columns = [
        {
            Header: "Outlet",
            accessor: "outlet",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : ""}</div>
            ),
        },
        {
            Header: "Amount",
            accessor: "amount",
            Cell: (params) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {params.row.original.outlet === "HAOFA Tourbillon Singapore" ? "$" : "RM"}
                    {NumberFormatter.format(params.row.original.amount ? params.row.original.amount : 0)}
                </div>
            ),
        },
        {
            Header: "Sales",
            accessor: "unit",
            Cell: ({ value }) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value ? value : 0}</div>
            ),
        },
        {
            Header: "Action",
            accessor: "action",
            Cell: (params) => (
                <div className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {superadmin && (
                        <button
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                            onClick={() =>
                                history.push("/orders", {
                                    outlet: params.row.original.outletId,
                                })
                            }
                        >
                            View Sales
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="container mx-auto px-4 py-8 block">
            {/* Header Section */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex space-x-2">
                    {data[0] && (
                        <button
                            onClick={() => {
                                const payload = salesData.map((item) => {
                                    return {
                                        Outlet: item.outlet,
                                        Unit: item.unit,
                                        Amount: item.amount,
                                    };
                                });
                                excel({
                                    excelData: payload,
                                    fileName: `Haofa-Sales-${moment(
                                        inputs.dateFrom
                                    ).format("DD-MM-YYYY")}-to-${moment(
                                        inputs.dateTo
                                    ).format("DD-MM-YYYY")}`,
                                });
                            }}
                            className="flex items-center bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
                        >
                            <FiPlus className="mr-2" />
                            Export
                        </button>
                    )}
                    <button
                        onClick={handleResetFilters}
                        className="flex items-center bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
                    >
                        <FiPlus className="mr-2" />
                        Reset Filters
                    </button>
                </div>
            </div>

            {/* Filter Panel */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date From
                        </label>
                        <input
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            name="dateFrom"
                            value={inputs.dateFrom}
                            type="date"
                            onChange={(e) => handleDateFrom(e)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date To
                        </label>
                        <input
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            name="dateTo"
                            value={inputs.dateTo}
                            type="date"
                            onChange={(e) => handleDateTo(e)}
                        />
                    </div>
                    {superadmin && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Outlet
                            </label>
                            <div className="react-select-container">
                                <Select
                                    options={usersData.map((user, i) => {
                                        return {
                                            label: `${user.name} - ${user.phoneNumber} - ${user.email}`,
                                            value: user._id,
                                        };
                                    })}
                                    value={inputs.user !== undefined ? usersData.find(user => user._id === inputs.user) ? {
                                        label: `${usersData.find(user => user._id === inputs.user).name} - ${usersData.find(user => user._id === inputs.user).phoneNumber} - ${usersData.find(user => user._id === inputs.user).email}`,
                                        value: inputs.user
                                    } : null : null}
                                    onChange={(e) => handleChange(e)}
                                    placeholder="Select Outlet"
                                    onClick={() =>
                                        usersData[0] === undefined
                                            ? getAdmin()
                                            : {}
                                    }
                                    {...commonSelectProps}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            {!data[0] ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                    <h1 className="text-xl font-semibold text-gray-700">
                        No Sales has been made this month yet. Please check back later.
                    </h1>
                </div>
            ) : (
                <>
                    {/* Charts Section */}
                    {isLoaded && data[0] && (
                        <div className="bg-white rounded-lg shadow p-6 mb-6">
                            <div className="mb-6">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">Sales Overview</h2>
                                <div
                                    style={{
                                        height: window.innerHeight / 2,
                                        width: "100%",
                                    }}
                                >
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            width={500}
                                            height={500}
                                            stroke="#fff"
                                            fill="#fff"
                                            data={salesData}
                                            margin={{
                                                top: 5,
                                                right: 0,
                                                left: 0,
                                                bottom: 5,
                                            }}
                                        >
                                            <CartesianGrid
                                                stroke="#f5f5f5"
                                                strokeDasharray="3 3"
                                            />
                                            <XAxis dataKey="outlet" />
                                            <YAxis stroke="#fff" />
                                            <Tooltip />
                                            <Bar
                                                dataKey="unit"
                                                stackId="a"
                                                fill="#1f355e"
                                                label
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales Distribution</h3>
                                    <PieChart
                                        width={400}
                                        height={300}
                                    >
                                        <Pie
                                            data={salesData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="unit"
                                        >
                                            {salesData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={
                                                        COLORS[
                                                            index %
                                                                COLORS.length
                                                        ]
                                                    }
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                    </PieChart>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales Data</h3>
                                    <div className="bg-white rounded-lg shadow overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-orange-400">
                                                <tr>
                                                    {columns.map((column) => (
                                                        <th
                                                            key={column.Header}
                                                            className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider"
                                                        >
                                                            {column.Header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {salesData.map((sale, index) => (
                                                    <tr key={index} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {sale.outlet ? sale.outlet : ""}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {sale.outlet === "HAOFA Tourbillon Singapore" ? "$" : "RM"}
                                                            {NumberFormatter.format(sale.amount ? sale.amount : 0)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {sale.unit ? sale.unit : 0}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {superadmin && (
                                                                <button
                                                                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs"
                                                                    onClick={() =>
                                                                        history.push("/orders", {
                                                                            outlet: sale.outletId,
                                                                        })
                                                                    }
                                                                >
                                                                    View Sales
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
